import React, { useLayoutEffect, useRef } from 'react';
import Paper from '@material-ui/core/Paper';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
  FixedSizeList as List,
  ListChildComponentProps,
  ListOnItemsRenderedProps,
  ListOnScrollProps
} from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import styled from 'styled-components';
import Grow from '@material-ui/core/Grow';
import MuiMenuItem from '@material-ui/core/MenuItem';
import Popper from '@material-ui/core/Popper';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Icon from '@material-ui/core/Icon';
import ListItemText from '@material-ui/core/ListItemText';
import { PopperPlacementType } from '@material-ui/core/Popper';
import { SystemIcon } from '@utmost.co/utmost-design-system';
import { useSortedOptions } from '@utmost.co/utmost-design-system';
import { TypeaheadOption } from './types';
import { ExtraItemName } from './getVisibleOptions';
import { RecentItemsList } from './RecentItemsList';
import { useRecentItemsContext } from './RecentItemsProvider';

interface Props {
  anchorEl?: HTMLElement | null;
  focusedMenuItem: number | null;
  isOpenDropdown: boolean;
  itemSize: number;
  items: TypeaheadOption<any>[];
  keyDownOnMenuWrapper(e: React.KeyboardEvent): void;
  listRef: any;
  menuRef: any;
  multiple?: boolean;
  placement?: PopperPlacementType;
  selected?: TypeaheadOption<any>[];
  setValues(options: TypeaheadOption<any>[] | undefined, focusOnInput: boolean): void;
  sort?: boolean;
  /** When true, the list loads further pages of options as the user scrolls towards the end. */
  infiniteScroll?: boolean;
  /** Called when the user scrolls near the end of the options list. */
  onLoadMore?: () => void;
  /** Whether another page of options is available to load. */
  hasNextPage?: boolean;
  /** Whether a `onLoadMore` request is currently in flight. Drives the trailing loading row. */
  loadingMore?: boolean;
}

const RECENT_ITEMS_SHOW_THRESHOLD = 10;
const HEADER_HEIGHT = 32;

export const TypeaheadDropdown: React.FC<React.PropsWithChildren<Props>> = ({
  anchorEl,
  focusedMenuItem,
  isOpenDropdown,
  itemSize,
  items,
  keyDownOnMenuWrapper,
  listRef,
  menuRef,
  multiple,
  placement = 'top',
  selected,
  setValues,
  sort = false,
  infiniteScroll = false,
  onLoadMore,
  hasNextPage = false,
  loadingMore = false
}) => {
  const sortedOptions = useSortedOptions(items, sort);
  const { recentItems, addRecentItem, removeRecentItem } = useRecentItemsContext();

  // Preserve the list scroll position across selection re-renders. Selecting an item adds a
  // chip and reorders the recent items, which re-renders the dropdown and would otherwise
  // shift the list scroll. Track the user's last manual offset and restore it afterwards so
  // the list stays put while multi-selecting.
  const scrollOffsetRef = useRef(0);

  const handleListScroll = ({ scrollOffset, scrollUpdateWasRequested }: ListOnScrollProps) => {
    // Only record user-driven scrolls, not our own programmatic `scrollTo`/`scrollToItem`.
    if (!scrollUpdateWasRequested) {
      scrollOffsetRef.current = scrollOffset;
    }
  };

  const selectedCount = selected?.length ?? 0;
  const recentItemsKey = recentItems.map((item) => item.id).join(',');

  // selectedCount / recentItemsKey are intentional triggers (a selection re-renders the list),
  // not values read inside the effect.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional re-run triggers
  useLayoutEffect(() => {
    if (multiple && isOpenDropdown) {
      listRef.current?.scrollTo(scrollOffsetRef.current);
    }
  }, [selectedCount, recentItemsKey, multiple, isOpenDropdown, listRef]);

  // Calculate height for recent items section
  const recentItemsHeight =
    recentItems.length > 0
      ? recentItems.length * itemSize + HEADER_HEIGHT + 1 // items + header + divider
      : 0;

  // Calculate the max height for the main list, accounting for recent items
  // When recent items are present, increase the total available height to keep the main list usable
  const totalAvailableHeight = recentItems.length > 0 ? 500 : 400;
  const maxListHeight = totalAvailableHeight - recentItemsHeight;

  // When infinite scrolling, reserve a trailing row for the loading spinner while more pages remain.
  const showLoadingRow = infiniteScroll && (hasNextPage || loadingMore);
  const listItemCount = items.length + (showLoadingRow ? 1 : 0);
  const listHeight = Math.min(listItemCount * itemSize, maxListHeight);

  const handleSelectItem = (item: TypeaheadOption<any>, trackRecentItem: boolean) => {
    addRecentItem(item, trackRecentItem);

    if (multiple && selected) {
      if (selected.find((i) => i.id === item.id)) {
        setValues(
          selected.filter((i) => i.id !== item.id),
          true
        );
      } else {
        setValues(selected.concat(item), true);
      }
    } else {
      setValues([item], false);
    }
  };

  // Show recent items only when not all options are visible.
  // Do not show recent items if the dropdown is already displaying a small number of options (where they would be redundant),
  // or if '_INFO' is present (indicating the list is empty, not that all options are shown).
  // This ensures recent items appear only when there are more options available than currently shown.
  const showRecentItems =
    sortedOptions.length >= RECENT_ITEMS_SHOW_THRESHOLD || sortedOptions.some((item) => item.id === '_INFO');

  const renderRow = ({ index, style, data }: ListChildComponentProps) => {
    const item = data[index] as TypeaheadOption<any> | undefined;

    // The trailing row (no backing option) is the infinite-scroll loading indicator.
    if (!item) {
      return (
        <StyledLoadingRow style={style} data-test="select-menu-loading-more">
          <CircularProgress size={20} thickness={4} />
        </StyledLoadingRow>
      );
    }

    const isSelected = selected?.map((option) => option.id)?.includes(item.id) ?? false;

    return (
      // Dynamically set width of menu to longest menu item.
      <div style={{ ...style, width: 'auto', minWidth: style.width }}>
        {[ExtraItemName.INFO].includes(item.id as ExtraItemName) ? (
          <StyledMenuItem
            value=""
            data-test="select-menu-item-no-value"
            button
            disabled={item.disabled}
            selected={focusedMenuItem === index}
            size={itemSize}
            onClick={() => setValues([item], true)}
          >
            {item.label}
          </StyledMenuItem>
        ) : (
          <StyledMenuItem
            button
            selected={focusedMenuItem === index}
            data-test="select-menu-item"
            size={itemSize}
            data-test-value={item.label}
            onMouseDown={() => handleSelectItem(item, false)}
            value={item.value as any}
            style={{
              backgroundColor: isSelected && focusedMenuItem === index ? '#f0f1f2' : isSelected ? '#e1ebf5' : undefined
            }}
            key={item.id}
          >
            {multiple && isSelected ? (
              <ListItemIcon>
                <Icon>
                  <SystemIcon name="check" />
                </Icon>
              </ListItemIcon>
            ) : item.icon ? (
              <ListItemIcon>
                <Icon>{item.icon}</Icon>
              </ListItemIcon>
            ) : null}

            <StyledListItemText primary={item.label} secondary={item.subtitle ?? item.code}>
              <StyledOptionLabel>{item.label}</StyledOptionLabel>
            </StyledListItemText>
          </StyledMenuItem>
        )}
      </div>
    );
  };

  const renderList = (
    onItemsRendered?: (props: ListOnItemsRenderedProps) => void,
    listCallbackRef?: (instance: List | null) => void
  ) => (
    <List
      height={listHeight}
      itemCount={listItemCount}
      itemSize={itemSize}
      width="100%"
      itemData={sortedOptions}
      onItemsRendered={onItemsRendered}
      onScroll={handleListScroll}
      ref={(instance) => {
        // react-window's ref plus, when infinite-scrolling, the InfiniteLoader's own ref.
        listRef.current = instance;
        listCallbackRef?.(instance);
      }}
    >
      {renderRow}
    </List>
  );

  const mainList =
    infiniteScroll && onLoadMore ? (
      <InfiniteLoader
        isItemLoaded={(index) => !hasNextPage || index < items.length}
        loadMoreItems={() => {
          onLoadMore();

          return Promise.resolve();
        }}
        itemCount={listItemCount}
      >
        {({ onItemsRendered, ref }) => renderList(onItemsRendered, ref)}
      </InfiniteLoader>
    ) : (
      renderList()
    );

  return (
    <Popper
      open={isOpenDropdown}
      anchorEl={anchorEl}
      transition
      role="presentation"
      data-test="select-menu"
      placement={placement}
      style={{ zIndex: 1300 }}
      popperOptions={{
        modifiers: {
          setPopperWidth: {
            enabled: true,
            order: 849,
            fn: setPopperWidth
          }
        }
      }}
    >
      {({ TransitionProps, placement }) => (
        <Grow {...TransitionProps} style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}>
          <Paper>
            {/* TODO: Prepare ActiveDiv for accessibility */}
            {/* eslint-disable-next-line */}
            <StyledDropdownContainer ref={menuRef} onKeyDown={keyDownOnMenuWrapper} tabIndex={1} role="list">
              {/* Recent Items Section */}
              {showRecentItems && (
                <RecentItemsList
                  recentItems={recentItems}
                  itemSize={itemSize}
                  selected={selected}
                  multiple={multiple}
                  onItemClick={(item) => handleSelectItem(item, true)}
                  onRemoveItem={removeRecentItem}
                />
              )}
              {/* Main Options List */}
              {mainList}
            </StyledDropdownContainer>
          </Paper>
        </Grow>
      )}
    </Popper>
  );
};

const StyledDropdownContainer = styled.div`
  max-height: 550px;
`;

const StyledMenuItem = styled(MuiMenuItem)<{ size: number }>`
  height: ${(props) => props.size}px;
  &:hover {
    background: #f0f1f2 !important;
  }
`;

const StyledListItemText = styled(ListItemText)`
  p {
    font-size: 10px;
    line-height: 12px;
  }
`;

export const StyledOptionLabel = styled.span`
  font-size: 14px;
  line-height: 20px;
`;

const StyledLoadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

function setPopperWidth(data: any) {
  const { width, left, right } = data.offsets.reference;
  data.styles.width = width;
  data.offsets.popper.width = width;
  data.offsets.popper.left = left;
  data.offsets.popper.right = right;
  return data;
}
